/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { type MessageDescriptor, FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { TWG_BRANDING } from '../../../../branding';

import graphVisualization from './assets/graph-visualization.webp';
import { messages } from './messages';

const VALUE_PILLARS: { title: MessageDescriptor; description: MessageDescriptor }[] = [
	{
		title: messages.contextBuiltYourWayTitle,
		description: messages.contextBuiltYourWayDescription,
	},
	{
		title: messages.aiReadyDataTitle,
		description: messages.aiReadyDataDescription,
	},
	{
		title: messages.contextThatTravelsTitle,
		description: messages.contextThatTravelsDescription,
	},
];

const styles = cssMap({
	pillarsRow: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		minHeight: '720px',
		'@media (max-width: 56.25rem)': {
			gridTemplateColumns: '1fr',
		},
	},
	graphic: {
		display: 'flex',
		alignItems: 'flex-end',
		overflow: 'hidden',
		'@media (max-width: 56.25rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			minHeight: '360px',
		},
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderRight: '2px dotted #505258',
	},
	graphicImage: {
		display: 'block',
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		mixBlendMode: 'lighten',
		pointerEvents: 'none',
	},
	pillars: {
		display: 'grid',
		gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
	},
	pillarCard: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		gap: token('space.200'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '30px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderBottom: '2px dotted #505258',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:last-of-type': {
			borderBottom: 'none',
		},
	},
	cardEyebrow: {
		font: token('font.body.small'),
		margin: 0,
		color: token('color.text.subtle'),
		fontFamily: token('font.family.code'),
		fontWeight: token('font.weight.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '1px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
	cardTitle: {
		margin: 0,
		font: token('font.heading.medium'),
		color: token('color.text.subtle'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '28px',
	},
	cardDescription: {
		font: token('font.body.large'),
		fontFamily: token('font.family.brand.body'),
		margin: 0,
		color: token('color.text.subtle'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '28px',
	},
});

export function Pillars(): JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.pillarsRow}>
			<div css={styles.graphic}>
				<img
					css={styles.graphicImage}
					src={graphVisualization}
					alt={formatMessage(messages.graphVisualizationAlt, { twg_branding: TWG_BRANDING })}
				/>
			</div>
			<div css={styles.pillars}>
				{VALUE_PILLARS.map((pillar) => (
					<div key={pillar.title.id} css={styles.pillarCard}>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={styles.cardEyebrow}>
							<FormattedMessage {...messages.headlineLabel} />
						</p>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={styles.cardTitle}>
							<FormattedMessage {...pillar.title} />
						</p>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={styles.cardDescription}>
							<FormattedMessage {...pillar.description} values={{ twg_branding: TWG_BRANDING }} />
						</p>
					</div>
				))}
			</div>
		</div>
	);
}
